import { Session } from '@ais-ui/auth';

export enum ReportBackendTypes {
  AREA = 'AREA_STAT_TB',
  MMSI_CHANGE = 'MMSI_CHANGE',
  ATON_DETAILED = 'ATON_DETAILED_REPORT',
  ATON_OVERVIEW = 'ATON_OVERVIEW_REPORT',
  CSV_DATA_EXPORT = 'CSV_DATA_EXPORT',
  CSV_DATA_EXPORT_FAST = 'DATA_EXPORT_TB',
  COVERAGE = 'COVERAGE',
  DENSITY = 'DENSITY_TB',
  FERRY_ROUTE = 'FERRY_ROUTE',
  FISHING = 'FISHING',
  TRACK = 'HISTORY_TRACK_TB',
  TRIPS = 'HISTORICAL_TRIPS',
  MULTI_SHAPE = 'MULTI_AREA',
  NEAR_MISS_SHIP_TO_POINT = 'NMSP2_TB',
  NEAR_MISS_SHIP_TO_SHIP = 'NEARMISSSHIPTOSHIP_TB',
  PSS_DETAILED = 'PSSSTATUS_DETAILED_REPORT',
  PSS_OVERVIEW = 'PSSSTATUS_OVERVIEW_REPORT',
  PASSAGELINE = 'PASSAGELINE_TB',
  PASSAGELINE_SHIPS = 'DATA_PASSAGELINE_MULTI',
  PILOT_OPERATIONS = 'PILOTBOAT_TRIPS',
  PORT_CALLS = 'PORT_CALLS',
  REPLAY_VIDEO = 'REPLAY_VIDEO',
  SOG_DISTRIBUTION = 'SOG_DISTRIBUTION',
  SQL = 'GENERIC_SQL',
  SHIPS = 'SHIPS_REPORT_TB',
  TRANSIT_PILOTAGE = 'TRANSIT_PILOTAGE',
  TRANSPONDER_OFF = 'TRANSPONDER_OFF',
  TUGBOAT_OPERATIONS = 'TUGBOAT_OPERATIONS',
  WATCHDOG_EVENTS = 'WDEVENT',
}

interface CommonParams {
  RDN: string; // RDN is the report template name
  RDU: string | null;
  NAME: string; // This is the name of the first generated report
  SDAUSR: string; // start time
  EDAUSR: string; // end time
  RS: {
    // schedule, this is always null for generating first report
    SI: string | null;
    SIS: number;
    EDAUSR: string;
    MAXKEPT: number;
    EXECTIME: string | null;
  } | null;
  MC: {
    // Always present, and always empty
    who: any[];
    action: string;
  };
  SUID: string;
  SR: any[];
  RSE?: boolean;
}

export interface AreaParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  STG: string;
  SGA: number;
  PI: string;
  GGD: number;
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
}

export interface ShipsParams extends CommonParams {
  TL: Array<{
    name: string | null;
    mmsi: string | null;
    callsign: string | null;
    imo: string | number | null;
  }>;
  IGPS: number;
  RSA: boolean;
}

export interface TrackParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  CB: string;
  CBCD: string;
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
}

export interface DensityParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  CB: string;
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
  SMML: number;
  SMMLMIN?: number;
  SMMLMAX?: number;
  CS: {
    id: number;
    sHue: number;
    eHue: number;
  };
  CPD: number;
  CD: number;
}

export interface NearMissShipToShipParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  MCD: number;
  NMSSAS: string;
  TL: Array<{
    name: string | null;
    mmsi: string | null;
    callsign: string | null;
    imo: string | number | null;
  }>;
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
}

export interface NearMissShipToPointParams extends CommonParams {
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
  LAT: number;
  LON: number;
  DT: number;
}

export interface CsvExportParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SHPL: string | null;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
  RSD: string;
  CSVP: string[];
  MSGT: string[];
  MF: number;
  SRATE: null;
}

export interface WatchdogParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  RSA: boolean;
  WD: string[];
}

export interface TripsParams extends CommonParams {
  DESTGAC: {
    GAC: {
      shape_id: string;
      shape: string;
    };
    WS: boolean;
  };
  SRCGAC: {
    GAC: {
      shape_id: string;
      shape: string;
    };
    WS: boolean;
  };
  SDAUSR: string;
  EDAUSR: string;
  MDUR: string;
  EL: number;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
}

export interface PassagelineParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  PAC: string;
  STG: string;
  TYP: string;
  PI: string;
  CAI: string;
  CC: boolean;
  MCI: string;
  MCPAD: number;
  DCDV: number;
  DCDU: string;
}

export interface PassagelineShipsParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  PAC: string;
  STG: string;
  TYP: string;
  PI: string;
  BDIR: number;
  MDUR: string;
}

export interface TransponderOffParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  MDUR: string;
}

export interface FishingParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  MIDUR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
}

export interface PilotParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  SHPOP: string;
}

export interface TransitPilotParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  SHPOP: string;
  MDUR: string;
}

export interface MultiShapeParams extends CommonParams {
  GAC: {
    shape_id: string;
    order: string[];
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
  PAC: string;
  STG: string;
  TYP: string;
  PI: string;
  BDIR: number;
  MDUR: string;
}

export interface SpeedOverGroundDistributionParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  SHPL: string | null;
  RSA: boolean;
  WS: number;
  TL: Array<{
    name: string | null;
    mmsi: string | null;
    callsign: string | null;
    imo: string | number | null;
  }>;
  STG: string;
  PI: string;
  EXMSG27: number;
}

export interface PortCallsParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  } | null;
  WS: number;
  PORT: number[];
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
}

export interface FerryRouteParams extends CommonParams {
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  SHPL: string | null;
  RSA: boolean;
}

export interface ReplayVideoParams extends CommonParams {
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  TL: Array<{
    name: string | null;
    mmsi: string | null;
    callsign: string | null;
    imo: string | number | null;
  }>;
  SHPL: string | null;
  VT: string;
  VS: number;
  GAC: {
    shape_id: string;
    shape: string;
  } | null;
  VR: {
    W: number;
    H: number;
  };
  VF: string;
  VQ: string;
  MAT: string;
  SAS: number;
  ST: false;
  TLT: number;
  VC: number;
  VCE: object[];
}

export interface SqlParams extends CommonParams {
  SDAUSR: string;
  EDAUSR: string;
  GAC: {
    shape_id: string;
    shape: string;
  } | null;
  ST: false;
  SQL: string;
}

export interface CoverageParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SDAUSR: string;
  EDAUSR: string;
  FID: string;
  FN: string;
  FI: string;
  RSA: boolean;
  CCR: number;
  CR: number;
  CD: number;
  CPTYP: string;
}

export interface AtonDetailedParams extends CommonParams {
  RSA: boolean;
  MMSI: number;
}

export interface AtonOverviewParams extends CommonParams {
  RSA: boolean;
}

export interface MmsiChangeParams extends CommonParams {
  GAC: {
    shape_id: string;
    shape: string;
  };
  SGA: number;
  GGD: number;
  RSA: boolean;
}

export type ReportTypeToParams = {
  [K in ReportBackendTypes]: K extends ReportBackendTypes.AREA
    ? AreaParams
    : K extends ReportBackendTypes.SHIPS
    ? ShipsParams
    : K extends ReportBackendTypes.TRACK
    ? TrackParams
    : K extends ReportBackendTypes.TRIPS
    ? TripsParams
    : K extends ReportBackendTypes.DENSITY
    ? DensityParams
    : K extends ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP
    ? NearMissShipToShipParams
    : K extends ReportBackendTypes.PASSAGELINE
    ? PassagelineParams
    : K extends ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT
    ? NearMissShipToPointParams
    : K extends ReportBackendTypes.PORT_CALLS
    ? PortCallsParams
    : K extends ReportBackendTypes.PASSAGELINE_SHIPS
    ? PassagelineShipsParams
    : K extends ReportBackendTypes.TRANSPONDER_OFF
    ? TransponderOffParams
    : K extends ReportBackendTypes.SOG_DISTRIBUTION
    ? SpeedOverGroundDistributionParams
    : K extends ReportBackendTypes.FISHING
    ? FishingParams
    : K extends ReportBackendTypes.MULTI_SHAPE
    ? MultiShapeParams
    : K extends ReportBackendTypes.PILOT_OPERATIONS
    ? PilotParams
    : K extends ReportBackendTypes.COVERAGE
    ? CoverageParams
    : K extends ReportBackendTypes.FERRY_ROUTE
    ? FerryRouteParams
    : K extends ReportBackendTypes.CSV_DATA_EXPORT
    ? CsvExportParams
    : K extends ReportBackendTypes.WATCHDOG_EVENTS
    ? WatchdogParams
    : K extends ReportBackendTypes.REPLAY_VIDEO
    ? ReplayVideoParams
    : K extends ReportBackendTypes.SQL
    ? SqlParams
    : K extends ReportBackendTypes.ATON_DETAILED
    ? AtonDetailedParams
    : K extends ReportBackendTypes.ATON_OVERVIEW
    ? AtonOverviewParams
    : K extends ReportBackendTypes.MMSI_CHANGE
    ? MmsiChangeParams
    : never;
};

// Keys that belong to UI settings and should never be sent to backend as report params
const SETTINGS_KEYS_TO_FILTER = [
  'assetDisplay.mapengine',
  'ribbonPlugin.settings',
  'wmsSettings',
  'vectorTiles',
  'homeBookmark',
  'show_ports',
  'favorite_port',
  'favorite_port_data',
  'showTargetCountBadge',
  'user_settings',
  'bookmarks',
];

/**
 * Removes any UI settings keys that may have accidentally been included in report params.
 * This is a safety measure to prevent settings from being stored with reports.
 */
function sanitizeReportParams<T extends object>(params: T): T {
  const sanitized = { ...params };
  for (const key of SETTINGS_KEYS_TO_FILTER) {
    if (key in sanitized) {
      delete (sanitized as Record<string, unknown>)[key];
    }
  }
  return sanitized;
}

export async function CreateReportTemplateAndReport<
  T extends ReportBackendTypes
>(session: Session, type: T, params: ReportTypeToParams[T]): Promise<string> {
  const sanitizedParams = sanitizeReportParams(params);
  return await session.request('createJob', {
    type: type,
    params: JSON.stringify(sanitizedParams),
    action: 'CreateJobAndUpdateTemplate',
  });
}

export async function UpdateScheduleForReportTemplate(
  session: Session,
  params: object
): Promise<string> {
  return await session.request('scheduleJob', {
    ...params,
  });
}

export async function CloneReport<T extends ReportBackendTypes>(
  session: Session,
  type: T,
  params: ReportTypeToParams[T]
): Promise<string> {
  const sanitizedParams = sanitizeReportParams(params);
  const response = await session.request('createJob', {
    type: type,
    params: JSON.stringify(sanitizedParams),
    action: 'OnlyCreateJob',
  });

  return response;
}

export async function ShareReport<T extends ReportBackendTypes>(
  session: Session,
  type: T,
  params: ReportTypeToParams[T]
): Promise<string> {
  const sanitizedParams = sanitizeReportParams(params);
  const response = await session.request('createJob', {
    type: type,
    params: JSON.stringify(sanitizedParams),
    action: 'OnlyUpdateTemplate',
  });

  return response;
}
